<template>
  <section
    v-if="variants.v1 === variant && sidebarComponents.length === 0"
    class="layout-pt-lg layout-pb-lg"
    :style="`background-color: var(--c-${vars.backgroundColor}); color: ${vars.textColor ? `var(--c-${vars.textColor})` : 'var(--c-text-primary)'}`"
  >
    <div class="container">
      <div class="row">
        <div class="col-lg-4 col-md-12 tw-mt-4">
          <h2 class="text-uppercase">{{ vars.titleText }}</h2>
          <div class="mt-20">
            <h6 style="color: var(--c-secondary)">{{ vars.descriptionText }}</h6>
          </div>

          <div class="mt-30 d-none d-lg-block">
            <a :href="ROUTES.BLOGS" class="n-secondary">
              {{ 'EXPLORE&nbsp;ALL&nbsp;ARTICLES' }}
            </a>
          </div>
        </div>

        <div class="col-lg-8 col-md-12">
          <div v-if="isBlogsLoading">
            <loader loader-color="var(--c-primary)" />
          </div>
          <div class="row">
            <div class="col-md-6 col-sm-12 mb-md-30 mb-15" v-for="(blog, index) in blogs" :key="`blog-${index}`">
              <Theme8BlogsBlogCard :blog="blog" :index="index" :component="component" />
            </div>
          </div>

          <div class="mt-30 d-block d-lg-none text-start">
            <a :href="ROUTES.BLOGS" class="n-secondary">
              {{ 'EXPLORE&nbsp;ALL&nbsp;ARTICLES' }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>

  <div v-else-if="sidebarComponents.length > 0" :style="`background-color: var(--c-${vars.backgroundColor})`">
    <div
      class="tw-grid tw-w-full tw-grid-cols-12 tw-grid-rows-1 tw-items-center tw-p-7"
      :style="`color:white`"
      style="border-radius: var(--rounded)"
    >
      <h3 class="tw-col-span-12 tw-mb-4">OUR BLOG</h3>
      <div class="swiper-wrapper tw-col-span-12">
        <Swiper
          :key="'blog-swiper-' + blogs.length"
          :modules="[SwiperPagination]"
          :pagination="false"
          :slides-per-view="1"
          :space-between="10"
          effect="fade"
          :clickable="true"
          :watch-overflow="true"
          @slideChange="onSlideChange"
          @swiper="onSwiper"
          :breakpoints="{
            320: { slidesPerView: 1, spaceBetween: 10 },
          }"
          class="tw-w-full"
        >
          <SwiperSlide v-for="(blog, index) in blogs" :key="`latestblogs-blog-${index}`" class="tw-w-full">
            <Theme8BlogsBlogCard :blog="blog" :index="index" :component="component" />
          </SwiperSlide>
        </Swiper>
      </div>
      <div class="tw-col-span-12 tw-mt-6 tw-flex tw-items-center tw-justify-center tw-gap-4">
        <div v-if="canPaginateBack" @click="swiper.slidePrev()" class="arrow-button tw-cursor-pointer">
          <svg width="61" height="27" viewBox="0 0 61 27" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10.4697 14.0303C10.1768 13.7374 10.1768 13.2626 10.4697 12.9697L15.2426 8.1967C15.5355 7.90381 16.0104 7.90381 16.3033 8.1967C16.5962 8.48959 16.5962 8.96447 16.3033 9.25736L12.0607 13.5L16.3033 17.7426C16.5962 18.0355 16.5962 18.5104 16.3033 18.8033C16.0104 19.0962 15.5355 19.0962 15.2426 18.8033L10.4697 14.0303ZM61 14.25H11V12.75H61V14.25Z"
              fill="#BFA87E"
            />
            <circle
              cx="13.5"
              cy="13.5"
              r="13"
              transform="matrix(-1 0 0 1 27 0)"
              stroke="#BFA87E"
              stroke-opacity="0.5"
            />
          </svg>
        </div>
        <div v-if="canPaginateNext" @click="swiper.slideNext()" class="arrow-button tw-cursor-pointer">
          <svg width="61" height="27" viewBox="0 0 61 27" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M50.5303 14.0303C50.8232 13.7374 50.8232 13.2626 50.5303 12.9697L45.7574 8.1967C45.4645 7.90381 44.9896 7.90381 44.6967 8.1967C44.4038 8.48959 44.4038 8.96447 44.6967 9.25736L48.9393 13.5L44.6967 17.7426C44.4038 18.0355 44.4038 18.5104 44.6967 18.8033C44.9896 19.0962 45.4645 19.0962 45.7574 18.8033L50.5303 14.0303ZM0 14.25H50V12.75H0V14.25Z"
              fill="#BFA87E"
            />
            <circle cx="47.5" cy="13.5" r="13" stroke="#BFA87E" stroke-opacity="0.5" />
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import type { Component } from '~/types';
import indexGlobal from '~/mixins/index.global';
import { BlogsMixin } from '~/units/blogs/mixins/blogs.mixin';
import { ComponentMixin } from '~/mixins/component.mixin';
import { useBlogsStore } from '~/units/blogs/store';
import { useWebsiteStore } from '~/units/website/store';
import { ROUTES } from '~/constants/ui.constants';
import Loader from '~/components/common/Loader.vue';

export default defineNuxtComponent({
  name: 'Theme8OurBlog',

  mixins: [BlogsMixin, indexGlobal, ComponentMixin],

  components: {
    Loader,
  },

  data() {
    return {
      variants: {
        v1: 'v1',
        v2: 'v2',
      },
      canPaginateBack: true,
      canPaginateNext: true,
    };
  },

  async setup(props: { component: Component }) {
    const blogsStore = useBlogsStore();
    await blogsStore.fetchBlogsInPage(props.component);
  },

  computed: {
    ...mapState(useWebsiteStore, ['sidebarComponents']),
    ROUTES() {
      return ROUTES;
    },
    cards() {
      return this.groupedVariables.cards;
    },
  },

  methods: {
    onSwiper(swiper: object) {
      this.swiper = swiper;
    },

    onSlideChange() {
      if (!this.swiper) return;
      const isAtStart = this.swiper.isBeginning;
      const isAtEnd = this.swiper.isEnd;
      if (isAtStart) this.canPaginateBack = false;
      else this.canPaginateBack = true;
      if (isAtEnd) this.canPaginateNext = false;
      else this.canPaginateNext = true;
    },
  },
});
</script>
